import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import SearchIcon from '@/assets/icons/search.svg?react'
import { Checkbox, Dropdown, Input, Loading, Pagination, Trans } from '@/components'
import { EmptyView } from '@/components/EmptyView'

import { fetchCatalogModels, fetchProviderOptions } from '../../api'
import { CapabilityTag } from '../../components/CapabilityTag'
import { CAPABILITY_OPTIONS, CAPABILITY_OPTIONS_COMING_SOON } from '../../constants'
import { CatalogModel, RouteModelType } from '../../types'

const PAGE_SIZE = 5
const ALL = 'all'

interface ModelSelectTableProps {
  routeType: RouteModelType
  selection: Map<string, CatalogModel>
  onToggle: (models: CatalogModel[], checked: boolean) => void
}

export function ModelSelectTable({ routeType, selection, onToggle }: ModelSelectTableProps) {
  const { t } = useTranslation('modelRoute')
  const [provider, setProvider] = useState<string>(ALL)
  const [capabilities, setCapabilities] = useState<string[]>([ALL])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const capabilityFilter = capabilities.filter((c) => c !== ALL)

  const { data: providerOptions } = useQuery({
    queryKey: ['modelVendors'],
    queryFn: fetchProviderOptions,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['catalogModels', routeType, provider, capabilityFilter, search, page],
    queryFn: () =>
      fetchCatalogModels({
        routeType,
        provider: provider === ALL ? undefined : provider,
        capabilities: capabilityFilter,
        search,
        page,
        pageSize: PAGE_SIZE,
      }),
  })

  const models = data?.items ?? []
  const total = data?.total ?? 0
  const pageAllChecked = models.length > 0 && models.every((m) => selection.has(m.id))
  const pageSomeChecked = models.some((m) => selection.has(m.id))

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end justify-between gap-4">
        <div className="flex gap-8">
          <div className="flex flex-col gap-2">
            <div className="text-secondary t-sb">
              <Trans ns="modelRoute" i18nKey="create.filterProvider">
                Provider
              </Trans>
            </div>
            <Dropdown
              rootClassName="t-sb w-40"
              className="h-11"
              showAllOption
              allOptionLabel={t('create.providerAny', 'Any')}
              options={(providerOptions ?? []).map((p) => ({
                label: p.label,
                value: p.value,
                className: 't-sm',
              }))}
              value={provider}
              onChange={(value) => {
                setProvider(String(value))
                setPage(1)
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-secondary t-sb">
              <Trans ns="modelRoute" i18nKey="create.filterCapabilities">
                Capabilities
              </Trans>
            </div>
            <Dropdown
              rootClassName="t-sb w-40"
              className="h-11"
              multiple
              showAllOption
              options={[
                ...CAPABILITY_OPTIONS.map((c) => ({ label: c, value: c, className: 't-sm' })),
                ...CAPABILITY_OPTIONS_COMING_SOON.map((c) => ({
                  label: c,
                  value: c,
                  className: 't-sm',
                  disabled: true,
                })),
              ]}
              value={capabilities}
              onChange={(value) => {
                setCapabilities((Array.isArray(value) ? value : [value]).map(String))
                setPage(1)
              }}
            />
          </div>
        </div>
        <div className="relative w-60">
          <Input
            className="h-11 pl-9 t-sm"
            placeholder={t('create.searchPlaceholder', 'Search')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
          {/* after Input + z-10: Input's own relative wrapper would otherwise paint over the icon */}
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tertiary pointer-events-none z-10" />
        </div>
      </div>

      {isLoading && (
        <div className="w-full flex items-center justify-center p-8">
          <Loading />
        </div>
      )}
      {!isLoading && models.length === 0 && (
        <EmptyView
          imageSize="small"
          title={
            <span className="t-sm text-secondary">
              <Trans i18nKey="common.emptyViewNoDataMsg" />
            </span>
          }
        />
      )}
      {!isLoading && models.length > 0 && (
        <table className="table table-fixed">
          <thead>
            <tr>
              <th className="w-12">
                <Checkbox
                  checked={pageAllChecked}
                  indeterminate={!pageAllChecked && pageSomeChecked}
                  onChange={(checked) => onToggle(models, checked)}
                />
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="create.headerModel">
                  Model
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="create.headerProvider">
                  Provider
                </Trans>
              </th>
              <th colSpan={2}>
                <Trans ns="modelRoute" i18nKey="create.headerCapabilities">
                  Capabilities
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="create.headerInputPrice">
                  Input Price -1M
                </Trans>
              </th>
              <th>
                <Trans ns="modelRoute" i18nKey="create.headerOutputPrice">
                  Output Price -1M
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <tr key={model.id}>
                <td className="w-12">
                  <Checkbox
                    checked={selection.has(model.id)}
                    onChange={(checked) => onToggle([model], checked)}
                  />
                </td>
                <td className="t-sm t-sb">{model.model}</td>
                <td className="t-sm t-sb">{model.provider}</td>
                <td className="t-sm" colSpan={2}>
                  <div className="flex flex-wrap gap-2">
                    {model.capabilities.map((capability) => (
                      <CapabilityTag key={capability}>{capability}</CapabilityTag>
                    ))}
                  </div>
                </td>
                <td className="t-sm">
                  <span className="t-sb">{model.inputPrice}</span>{' '}
                  <span className="text-secondary">
                    <Trans ns="modelRoute" i18nKey="table.credits">
                      Credits
                    </Trans>
                  </span>
                </td>
                <td className="t-sm">
                  <span className="t-sb">{model.outputPrice}</span>{' '}
                  <span className="text-secondary">
                    <Trans ns="modelRoute" i18nKey="table.credits">
                      Credits
                    </Trans>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex items-center justify-between">
        <span className="t-sm text-secondary">
          <Trans
            ns="modelRoute"
            i18nKey="create.selectedCount"
            values={{ selected: selection.size, total }}
          >
            {'Selected {{selected}} of {{total}} items'}
          </Trans>
        </span>
        {total > 0 && (
          <Pagination size={PAGE_SIZE} total={total} current={page} onChange={setPage} />
        )}
      </div>
    </div>
  )
}
